import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const MANUAL_SOURCE = "ebay_product_research_manual";
const AGENT_SOURCE = "ebay_product_research_agent";

function buildExclusionQuery(exclusionTerms) {
  return String(exclusionTerms || "")
    .split(",")
    .map((term) => term.trim().replace(/^-+/, ""))
    .filter(Boolean)
    .map((term) => `-${term}`)
    .join(" ");
}

export function buildProductResearchQuery(card) {
  const exclusions = buildExclusionQuery(card?.exclusion_terms);
  return [String(card?.card_name || "").trim(), exclusions].filter(Boolean).join(" ").trim();
}

export function buildProductResearchUrl(card) {
  const endDate = Date.now();
  const startDate = endDate - 30 * 24 * 60 * 60 * 1000;
  const params = new URLSearchParams({
    marketplace: "EBAY-AU",
    keywords: buildProductResearchQuery(card),
    dayRange: "30",
    endDate: String(endDate),
    startDate: String(startDate),
    categoryId: "0",
    offset: "0",
    limit: "50",
    tabName: "SOLD",
    tz: "Australia/Sydney",
  });
  return `https://www.ebay.com.au/sh/research?${params.toString()}`;
}

function displaySource(source) {
  if (source === MANUAL_SOURCE) return "Manual Product Research";
  if (source === AGENT_SOURCE) return "Agent Product Research";
  if (source) return source;
  return "No source recorded";
}

export default function ManualProductResearchDialog({
  open,
  card,
  onClose,
  onSaved,
}) {
  const [soldCount, setSoldCount] = useState("");
  const [averagePrice, setAveragePrice] = useState("");
  const [lowPrice, setLowPrice] = useState("");
  const [highPrice, setHighPrice] = useState("");
  const [activeListingsCount, setActiveListingsCount] = useState("");
  const [confirmNoSales, setConfirmNoSales] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copyMessage, setCopyMessage] = useState("");

  const researchQuery = useMemo(() => buildProductResearchQuery(card), [card]);
  const researchUrl = useMemo(() => buildProductResearchUrl(card), [card]);

  useEffect(() => {
    if (!open || !card) return;
    setSoldCount(card.sales_count_30_actual ?? "");
    setAveragePrice(card.avg_price ?? "");
    setLowPrice(card.low_price_no_shipping ?? "");
    setHighPrice(card.high_price_no_shipping ?? "");
    setActiveListingsCount("");
    setConfirmNoSales(false);
    setError("");
    setCopyMessage("");
  }, [open, card]);

  const openProductResearch = () => {
    const researchWindow = window.open(researchUrl, "ebay-product-research");
    researchWindow?.focus?.();
  };

  const copyQuery = async () => {
    try {
      await navigator.clipboard.writeText(researchQuery);
      setCopyMessage("Search query copied.");
    } catch (copyError) {
      setCopyMessage("Copy failed. Select the query text manually.");
    }
  };

  const validate = () => {
    if (soldCount === "") return "Sold count is required.";
    const sold = Number(soldCount);
    if (!Number.isInteger(sold) || sold < 0) return "Sold count must be a non-negative whole number.";

    const parseOptional = (value, label) => {
      if (value === "") return null;
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0) {
        throw new Error(`${label} must be a non-negative number.`);
      }
      return parsed;
    };

    try {
      const average = parseOptional(averagePrice, "Average sold price");
      const low = parseOptional(lowPrice, "Low sold price");
      const high = parseOptional(highPrice, "High sold price");
      const active = parseOptional(activeListingsCount, "Active listing count");
      if (active !== null && !Number.isInteger(active)) {
        return "Active listing count must be a whole number.";
      }
      if (sold === 0) {
        if (!confirmNoSales) return "Confirm that Product Research showed no matching sales.";
        if (average !== null || low !== null || high !== null) {
          return "Leave sold price fields blank when sold count is zero.";
        }
      } else if (average === null) {
        return "Average sold price is required when sold count is above zero.";
      }
      if (low !== null && average !== null && low > average) {
        return "Low sold price cannot be greater than average sold price.";
      }
      if (high !== null && average !== null && average > high) {
        return "Average sold price cannot be greater than high sold price.";
      }
      if (low !== null && high !== null && low > high) {
        return "Low sold price cannot be greater than high sold price.";
      }
    } catch (validationError) {
      return validationError.message;
    }
    return "";
  };

  const save = async (saveAndNext) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        sold_count: Number(soldCount),
        average_price: averagePrice === "" ? null : Number(averagePrice),
        low_price: lowPrice === "" ? null : Number(lowPrice),
        high_price: highPrice === "" ? null : Number(highPrice),
        active_listings_count: activeListingsCount === "" ? null : Number(activeListingsCount),
        confirm_no_sales: confirmNoSales,
      };
      const response = await fetch(
        `/api/card-search-inputs/${card.id}/manual-product-research`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || "Failed to save Product Research data");
      }
      await onSaved?.(result, { saveAndNext });
    } catch (saveError) {
      setError(saveError?.message || "Failed to save Product Research data.");
    } finally {
      setSaving(false);
    }
  };

  const soldCountIsZero = soldCount !== "" && Number(soldCount) === 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Product Research — {card?.card_name || "Card"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Chip size="small" label={card?.search_type || "Unknown type"} />
            <Chip size="small" variant="outlined" label={displaySource(card?.data_source)} />
            {card?.summary_captured_at ? (
              <Typography variant="caption" color="text.secondary">
                Latest summary: {new Date(card.summary_captured_at).toLocaleString()}
              </Typography>
            ) : null}
          </Stack>

          <Alert severity="info">
            Open eBay Product Research, then visually confirm the Sold tab, Last 30 days, condition, location and any locked filters before entering values. Prices in this form exclude postage.
          </Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Product Research query
            </Typography>
            <TextField fullWidth value={researchQuery} InputProps={{ readOnly: true }} />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={openProductResearch}>
                Open Product Research
              </Button>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyQuery}>
                Copy Search Query
              </Button>
              {copyMessage ? (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                  {copyMessage}
                </Typography>
              ) : null}
            </Stack>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              required
              fullWidth
              label="Sold count — 30 days"
              type="number"
              value={soldCount}
              onChange={(event) => {
                const value = event.target.value;
                setSoldCount(value);
                setConfirmNoSales(false);
                if (value === "0") {
                  setAveragePrice("");
                  setLowPrice("");
                  setHighPrice("");
                }
              }}
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              required={!soldCountIsZero}
              fullWidth
              label="Average sold price (ex postage)"
              type="number"
              value={averagePrice}
              onChange={(event) => setAveragePrice(event.target.value)}
              disabled={soldCountIsZero}
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Low sold price (optional)"
              type="number"
              value={lowPrice}
              onChange={(event) => setLowPrice(event.target.value)}
              disabled={soldCountIsZero}
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              label="High sold price (optional)"
              type="number"
              value={highPrice}
              onChange={(event) => setHighPrice(event.target.value)}
              disabled={soldCountIsZero}
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Stack>

          <TextField
            label="Active listing count override (optional)"
            type="number"
            value={activeListingsCount}
            onChange={(event) => setActiveListingsCount(event.target.value)}
            helperText={
              card?.active_listings_count == null
                ? "Leave blank to keep no active-count value."
                : `Leave blank to preserve the latest active count (${card.active_listings_count}).`
            }
            inputProps={{ min: 0, step: 1 }}
          />

          {soldCountIsZero ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmNoSales}
                  onChange={(event) => setConfirmNoSales(event.target.checked)}
                />
              }
              label="I confirm Product Research showed no matching sales for this query and filter set."
            />
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={() => save(false)} disabled={saving} variant="outlined">
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button onClick={() => save(true)} disabled={saving} variant="contained">
          {saving ? "Saving…" : "Save & Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
